import { ZONA_DEL_NEGOCIO } from './zona';
import type { Masterclass, MonitoreoDeMasterclass } from '@/api/masterclass.api';

/**
 * REGLAS PURAS DE LA PANTALLA DE MASTERCLASS, probadas sin navegador.
 *
 * Aquí vive lo que decide QUÉ se ve y en qué tono; la pantalla solo lo dibuja. Es la
 * convención del repo, y además es lo que permite probar la parte que importa —cuándo se
 * avisa de algo y cuándo no— sin montar un DOM.
 */

/** Cuándo es, en la hora del negocio: «martes 14 de octubre, 8:00 p. m.». PURO. */
export function cuandoEs(iso: string): string {
  const f = new Date(iso);
  if (Number.isNaN(f.getTime())) return '';
  return new Intl.DateTimeFormat('es', {
    timeZone: ZONA_DEL_NEGOCIO,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: '2-digit',
  }).format(f);
}

/** Cómo se lee cada estado. PURO. */
export const ETIQUETA_DE_ESTADO: Record<string, string> = {
  PROGRAMADA: 'Programada',
  HECHA: 'Ya pasó',
  CANCELADA: 'Cancelada',
};

export type TonoDeFila = 'ok' | 'ojo' | 'alerta';

/**
 * EN QUÉ TONO SE VE CADA EDICIÓN, que es lo único que el equipo mira de reojo.
 *
 *  - `alerta`: hay gente marcada como que asistió y el lote sin sacar. Esas parejas no están
 *    siendo llamadas, y eso es plata que se queda en la mesa.
 *  - `ojo`: ya pasó y nadie marcó quién entró (la lista se está perdiendo), o está programada
 *    sin enlace de Zoom (el agente no lo puede prometer).
 *  - `ok`: todo lo demás.
 *
 * PURO.
 */
export function tonoDe(m: Masterclass): TonoDeFila {
  if (m.asistieron > 0 && !m.loteHechoAt) return 'alerta';
  if (m.estado === 'HECHA' && m.inscritos > 0 && m.asistieron === 0) return 'ojo';
  if (m.estado === 'PROGRAMADA' && !m.zoomUrl) return 'ojo';
  return 'ok';
}

/** Por qué está en ese tono, en una frase. `null` = no hay nada que decir. PURO. */
export function porQueEseTono(m: Masterclass): string | null {
  switch (tonoDe(m)) {
    case 'alerta':
      return `${m.asistieron === 1 ? 'Una pareja asistió' : `${m.asistieron} parejas asistieron`} y nadie las ha llamado: saca el lote.`;
    case 'ojo':
      return m.estado === 'PROGRAMADA' ? 'Sin el enlace de Zoom el agente no lo puede prometer.' : 'Ya pasó y nadie marcó quién entró.';
    default:
      return null;
  }
}

/** ¿Se le puede sacar el lote? Solo si hay a quién llamar y no se sacó ya. PURO. */
export const puedeSacarLote = (m: Masterclass): boolean => m.asistieron > 0 && !m.loteHechoAt;

/** «12 apuntados · 7 entraron». PURO. */
export function conteoDe(m: Masterclass): string {
  const apuntados = `${m.inscritos} ${m.inscritos === 1 ? 'apuntado' : 'apuntados'}`;
  if (m.estado === 'PROGRAMADA') return apuntados;
  return `${apuntados} · ${m.asistieron} ${m.asistieron === 1 ? 'entró' : 'entraron'}`;
}

/**
 * LO QUE HAY QUE HACER HOY, en una línea por cosa y en orden de importancia.
 *
 * Es lo que se pone ARRIBA de la lista: si está vacío, no se dibuja nada. Un aviso que
 * aparece siempre deja de ser un aviso. PURO.
 */
export function queHayQueHacer(m: MonitoreoDeMasterclass): string[] {
  const out: string[] = [];
  // Primero lo que nadie vería de otra manera: el lote que quedó a medias. La edición se ve
  // «hecha» y en orden, y esas parejas no se llaman nunca.
  for (const l of m.loteIncompleto ?? []) {
    out.push(
      `«${l.titulo}»: el lote quedó a medias (${l.creadas} de ${l.asistieron}). ` +
        'Escríbele a Esteban: hay parejas sin su llamada y hay que crearlas a mano.',
    );
  }
  for (const l of m.loteSinSacar) {
    out.push(`«${l.titulo}»: ${l.asistieron === 1 ? '1 pareja asistió' : `${l.asistieron} parejas asistieron`} y nadie las ha llamado. Saca el lote.`);
  }
  for (const s of m.sinAsistencia) {
    out.push(`«${s.titulo}» ya pasó y tiene ${s.inscritos} apuntados sin marcar. Marca quién entró.`);
  }
  for (const e of m.sinEnlace) {
    out.push(`«${e.titulo}» no tiene enlace de Zoom. Ponlo antes de que el agente la ofrezca.`);
  }
  return out;
}

/** El valor que el campo `datetime-local` entiende, en la hora del negocio. PURO. */
export function paraElCampo(iso: string): string {
  const f = new Date(iso);
  if (Number.isNaN(f.getTime())) return '';
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA_DEL_NEGOCIO,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(f);
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}`;
}

export interface FormularioDeMasterclass {
  titulo: string;
  /** Lo que escribe el campo `datetime-local`: 'YYYY-MM-DDTHH:MM', en hora del negocio. */
  inicio: string;
  zoomUrl: string;
  /** Vacío = sin tope. */
  cupo: string;
}

export const FORMULARIO_VACIO: FormularioDeMasterclass = { titulo: '', inicio: '', zoomUrl: '', cupo: '' };

/**
 * Qué está mal del formulario, por campo. `{}` = se puede guardar.
 *
 * El enlace se valida AQUÍ además de en el servidor, y no por desconfianza: un enlace a
 * medias guardado es un enlace que el agente promete y el cliente no puede abrir. Decirlo
 * antes de guardar cuesta menos que descubrirlo cuando alguien no entró a la clase. PURO.
 */
export function erroresDelFormulario(f: FormularioDeMasterclass): Record<string, string> {
  const e: Record<string, string> = {};
  if (!f.titulo.trim()) e.titulo = 'Ponle un nombre.';
  if (!f.inicio.trim() || Number.isNaN(Date.parse(f.inicio))) e.inicio = 'Pon el día y la hora.';
  const url = f.zoomUrl.trim();
  if (url && !/^https:\/\/\S+$/i.test(url)) e.zoomUrl = 'El enlace tiene que empezar por https://';
  const cupo = f.cupo.trim();
  if (cupo && !/^\d+$/.test(cupo)) e.cupo = 'Solo el número, o déjalo vacío.';
  return e;
}

/**
 * El formulario como lo quiere la API.
 *
 * `inicio` viaja con la zona del negocio pegada: el campo del navegador da una hora sin zona
 * («2026-10-14T20:00») y, si se manda así, el servidor la lee en SU zona. Esa es la clase de
 * error que mueve una masterclass una hora sin que nadie lo note. PURO.
 */
export function aDatos(f: FormularioDeMasterclass): { titulo: string; inicio: string; zoomUrl?: string; cupo?: number | null } {
  return {
    titulo: f.titulo.trim(),
    inicio: conLaZonaDelNegocio(f.inicio),
    ...(f.zoomUrl.trim() ? { zoomUrl: f.zoomUrl.trim() } : {}),
    cupo: f.cupo.trim() ? Number(f.cupo.trim()) : null,
  };
}

/**
 * 'YYYY-MM-DDTHH:MM' leído en la zona del negocio → ISO con su desplazamiento.
 *
 * Santo Domingo es UTC-4 todo el año y no tiene horario de verano, así que el desplazamiento
 * se calcula una vez y no cambia según el mes. Si el negocio se muda a una zona que sí lo
 * tenga, esto hay que mirarlo otra vez. PURO.
 */
export function conLaZonaDelNegocio(local: string): string {
  // `Date.parse` es demasiado permisivo: a «cualquier cosa:00Z» le encuentra una fecha. Se
  // exige la forma exacta que escribe el campo del navegador.
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) return local;
  const ingenuo = Date.parse(`${local}:00Z`);
  if (Number.isNaN(ingenuo)) return local;
  // Cuánto se desvía esa zona del UTC en ese instante, en minutos.
  const enLaZona = new Date(new Date(ingenuo).toLocaleString('en-US', { timeZone: ZONA_DEL_NEGOCIO }));
  const enUtc = new Date(new Date(ingenuo).toLocaleString('en-US', { timeZone: 'UTC' }));
  const desvioMin = Math.round((enLaZona.getTime() - enUtc.getTime()) / 60_000);
  return new Date(ingenuo - desvioMin * 60_000).toISOString();
}
